import React, { useState, useEffect } from "react";
import { Calendar, Phone, MapPin, User, Plus, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { fetchProductList } from "../../store/thunk/productThunk";
import toast from "react-hot-toast";
import {
  customPrintLabel,
  fetchCustomPrintLabel,
} from "../../store/thunk/adminThunk";
import {
  formatPhoneNumber,
  formatPrice,
  formatToUSDate,
} from "../../hooks/commonFunction";
import { useNavigate, useSearchParams } from "react-router-dom";
import AdminHeader from "../../shared/navbar/AdminHeader";

const CustomPrintLabelPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const printID = searchParams.get("id");
  const [productData, setProductData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [printData, setPrintData] = useState(null);
  const { ProductList } = useSelector((state) => state.products);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    orderDate: "",
    address: "",
    products: [{ name: "", quantity: 1 }],
    totalPrice: "18.99",
    notes:
      "Keep refrigerated or frozen. Thaw in refrigerator or microwave. Cook Thoroughly. Mishandling or cooking the product improperly could cause illness.",
    isChecked: false,
    showLabel: {
      name: true,
      phone_number: true,
      delivery_date: false,
      notes: true,
      address: true,
    },
  });

  useEffect(() => {
    setLoading(true);
    dispatch(fetchProductList());
  }, []);

  useEffect(() => {
    const fetchCustomPrintLabelData = async () => {
      setLoading(true);
      try {
        const result = await dispatch(fetchCustomPrintLabel(printID)).unwrap();
        if (result?.data) {
          //console.log("setPrintData", result?.data);
          setLoading(false);
          setPrintData(result?.data);
          setShowPrintPreview(true);
        }
      } catch (error) {
        console.error("Failed to fetch partners list:", error);
        // Set empty array as fallback
        setPrintData([]);
      }
    };
    if (printID) {
      fetchCustomPrintLabelData();
    }
  }, [printID]);

  useEffect(() => {
    if (ProductList) {
      setProductData(ProductList);
      setLoading(false);
    }
  }, [ProductList]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const calculateTotalPrice = (products) => {
    let total = 0;
    products.forEach((product) => {
      if (product.name && productData) {
        const selectedProduct = Object.values(productData).find(
          (p) => `${p.weight}${p.weight_symbol} ${p.name}` === product.name
        );
        if (selectedProduct) {
          total += selectedProduct.price * product.quantity;
        }
      }
    });
    return total.toFixed(2);
  };

  const handleProductChange = (index, field, value) => {
    const updatedProducts = [...formData.products];
    updatedProducts[index] = {
      ...updatedProducts[index],
      [field]: value,
    };

    const newTotalPrice = calculateTotalPrice(updatedProducts);

    setFormData((prev) => ({
      ...prev,
      products: updatedProducts,
      totalPrice: newTotalPrice,
    }));
  };

  const addProduct = () => {
    setFormData((prev) => ({
      ...prev,
      products: [...prev.products, { name: "", quantity: 1 }],
    }));
  };

  const removeProduct = (index) => {
    if (formData.products.length > 1) {
      const updatedProducts = formData.products.filter((_, i) => i !== index);
      const newTotalPrice = calculateTotalPrice(updatedProducts);

      setFormData((prev) => ({
        ...prev,
        products: updatedProducts,
        totalPrice: newTotalPrice,
      }));
    }
  };

  const handlePrint = async () => {
    // Validate required fields based on selected checkboxes
    const requiredFields = [];

    if (formData.showLabel.name && !formData.name.trim()) {
      requiredFields.push("Name");
    }
    if (formData.showLabel.phone_number && !formData.phone.trim()) {
      requiredFields.push("Phone Number");
    }
    if (formData.showLabel.delivery_date && !formData.orderDate.trim()) {
      requiredFields.push("Order Date");
    }
    if (formData.showLabel.notes && !formData.notes.trim()) {
      requiredFields.push("Notes");
    }
    if (formData.showLabel.address && !formData.address.trim()) {
      requiredFields.push("Address");
    }

    if (requiredFields.length > 0) {
      toast.error(
        `Please fill in the following required fields: ${requiredFields.join(
          ", "
        )}`
      );
      return;
    }

    try {
      // Create products array with proper structure
      const products = formData.products
        .filter((product) => product.name) // Only include products that have been selected
        .map((product) => {
          const selectedProduct = Object.values(productData || {}).find(
            (p) => `${p.weight}${p.weight_symbol} ${p.name}` === product.name
          );

          return {
            product_id: selectedProduct?.id?.toString() || "",
            product_name: product.name,
            product_qty: product.quantity,
            product_price: selectedProduct?.price || 0,
          };
        });

      // Build payload with only selected fields
      const payload = {
        products: products,
        total: parseFloat(formData.totalPrice),
        show_label: formData.showLabel,
      };

      // Only add fields that are selected in show_label
      if (formData.showLabel.name) {
        payload.name = formData.name;
      }
      if (formData.showLabel.phone_number) {
        payload.phone_number = formData.phone;
      }
      if (formData.showLabel.delivery_date) {
        payload.order_date = formData.orderDate;
      }
      if (formData.showLabel.notes) {
        payload.notes = formData.notes;
      }
      if (formData.showLabel.address) {
        payload.address = formData.address;
      }

      // Call the API
      const result = await dispatch(customPrintLabel(payload)).unwrap();
      //console.log("result", result);
      if (result?.status === 0) {
        toast.error(result.message || "Failed to generate label");
        return;
      } else if (result?.status === 1) {
        // Store the print data and show preview
        setPrintData({
          ...payload,
          id: result?.data?.custom_print_label,
        });
        setShowPrintPreview(true);

        const printLabelId = result?.data?.custom_print_label;

        // Navigate to the custom print label page
        navigate(`/custom-print-label?id=${printLabelId || 2}`);

        toast.success("Label generated successfully!");
      }
    } catch (error) {
      console.error("Print API Error:", error);
      alert("Error sending print request. Please try again.");
    }
  };

  // Print Preview Component (like PrintLabelPage but without header)
  const PrintPreview = ({ data }) => (
    <div
      className="max-w-md bg-white rounded-lg shadow-lg overflow-hidden"
      style={{ width: "4in", height: "6in" }}
    >
      {/* Header */}
      <div className="bg-black text-white p-4 pr-5 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="flex items-center justify-center">
            <img
              src="https://images.halalfull.com/assets/img/logo-white.png"
              alt=""
              className="h-14"
            />
          </div>
        </div>
        <div className="flex items-center justify-center">
          <img
            src="https://images.halalfull.com/dev/assets/img/halal.png"
            alt=""
            className="h-14"
          />
        </div>
      </div>

      {/* Order Details */}
      <div className="p-4 space-y-3">
        {/* Order ID and Date */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 min-h-5"></div>
          {printData?.label?.date && (
            <div className="flex items-center space-x-2 text-gray-600">
              <Calendar className="w-4 h-4" />
              <span>{formatToUSDate(printData.label.date)}</span>
            </div>
          )}
        </div>

        {/* Customer Info */}
        <div className="space-y-3">
          <div className="flex items-center space-x-3 text-gray-700">
            <User className="w-4 h-4" />
            <span>{printData?.label?.name}</span>
          </div>

          <div className="flex items-center space-x-3 text-gray-700">
            <Phone className="w-4 h-4" />
            <span>{formatPhoneNumber(printData?.label?.phone_number)}</span>
          </div>

          <div className="flex items-start space-x-3 text-gray-700">
            <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
            <span className="text-sm leading-relaxed">
              {printData?.label?.address}
            </span>
          </div>
        </div>

        {/* Items */}
        <div className="border-t border-gray-200 pt-3">
          {printData?.label?.product_info &&
            Object.values(printData?.label?.product_info).map((item, index) => (
              <div key={item.product_id || index} className="text-gray-800">
                {item.product_qty} {item.weight_symbol} {" x "}
                {item.product_name}
              </div>
            ))}
        </div>

        {/* Total */}
        <div className="border-t border-gray-200 pt-4">
          <div className="flex items-center space-x-2">
            <span className="font-semibold text-gray-700">Total:</span>
            <span className="font-bold text-lg">
              {formatPrice(printData?.label?.total)}
            </span>
          </div>
        </div>

        {/* Special Instructions */}
        <div className="">
          <p className="text-sm leading-relaxed">
            <span className="font-semibold text-gray-800">
              Special Instructions:{" "}
            </span>
            {printData?.label?.notes}
          </p>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="max-w-lg mx-auto bg-white rounded-lg shadow-lg p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-20 bg-gray-200 rounded"></div>
          <div className="space-y-3">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Show print preview after successful API call
  if (showPrintPreview && printData) {
    return <PrintPreview data={printData} />;
  }

  return (
    <>
      <AdminHeader />
      <div className="max-w-lg mx-auto bg-white rounded-lg shadow-lg overflow-hidden mt-7">
        {/* Header */}
        <div className="bg-black text-white p-4 pr-5 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center">
              <img
                src="https://images.halalfull.com/assets/img/logo-white.png"
                alt=""
                className="h-14"
              />
            </div>
          </div>
          <div className="flex items-center justify-center">
            <img
              src="https://images.halalfull.com/dev/assets/img/halal.png"
              alt=""
              className="h-14"
            />
          </div>
        </div>

        {/* Form */}
        <div className="p-6 space-y-4">
          {/* Name Input */}
          <div className="relative">
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
              <User className="w-4 h-4 text-gray-400" />
            </div>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              className="w-full pl-10 pr-10 py-3 bg-gray-100 border-0 rounded-md focus:bg-white focus:ring-2 focus:ring-red-500 outline-none"
              placeholder="Customer Name"
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <input
                type="checkbox"
                checked={formData.showLabel.name}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    showLabel: { ...prev.showLabel, name: e.target.checked },
                  }))
                }
                className="w-5 h-5 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 focus:ring-2"
              />
            </div>
          </div>

          {/* Phone Input */}
          <div className="relative">
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
              <Phone className="w-4 h-4 text-gray-400" />
            </div>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange("phone", e.target.value)}
              className="w-full pl-10 pr-10 py-3 bg-gray-100 border-0 rounded-md focus:bg-white focus:ring-2 focus:ring-red-500 outline-none"
              placeholder="Phone Number"
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <input
                type="checkbox"
                checked={formData.showLabel.phone_number}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    showLabel: {
                      ...prev.showLabel,
                      phone_number: e.target.checked,
                    },
                  }))
                }
                className="w-5 h-5 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 focus:ring-2"
              />
            </div>
          </div>

          {/* Order Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Order Date
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                <Calendar className="w-4 h-4 text-gray-400" />
              </div>
              <input
                type="date"
                value={formData.orderDate}
                onChange={(e) => handleInputChange("orderDate", e.target.value)}
                className="w-full pl-10 pr-10 py-3 bg-gray-100 border-0 rounded-md focus:bg-white focus:ring-2 focus:ring-red-500 outline-none"
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <input
                  type="checkbox"
                  checked={formData.showLabel.delivery_date}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      showLabel: {
                        ...prev.showLabel,
                        delivery_date: e.target.checked,
                      },
                    }))
                  }
                  className="w-5 h-5 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 focus:ring-2"
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="relative">
            <div className="absolute left-3 top-4 z-10">
              <MapPin className="w-4 h-4 text-gray-400" />
            </div>
            <textarea
              value={formData.address}
              onChange={(e) => handleInputChange("address", e.target.value)}
              className="w-full pl-10 pr-10 py-3 bg-gray-100 border-0 rounded-md focus:bg-white focus:ring-2 focus:ring-red-500 outline-none resize-none"
              rows="2"
              placeholder="Delivery Address"
            />
            <div className="absolute right-3 top-4">
              <input
                type="checkbox"
                checked={formData.showLabel.address}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    showLabel: { ...prev.showLabel, address: e.target.checked },
                  }))
                }
                className="w-5 h-5 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 focus:ring-2"
              />
            </div>
          </div>

          {/* Products Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-red-600">Products</h3>
              <button
                onClick={addProduct}
                className="bg-red-600 text-white px-4 py-2 rounded-md text-sm flex items-center space-x-1 hover:bg-red-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Add</span>
              </button>
            </div>

            <div className="space-y-3">
              {formData.products.map((product, index) => (
                <div
                  key={index}
                  className="grid grid-cols-12 gap-2 items-center"
                >
                  <select
                    value={product.name}
                    onChange={(e) =>
                      handleProductChange(index, "name", e.target.value)
                    }
                    className="col-span-8 py-3 px-3 bg-gray-100 border-0 rounded-md focus:bg-white focus:ring-2 focus:ring-red-500 outline-none text-sm"
                  >
                    <option value="">Select Product</option>
                    {productData &&
                      Object.values(productData)?.map((productItem) => (
                        <option
                          key={productItem.id}
                          value={`${productItem.weight}${productItem.weight_symbol} ${productItem.name}`}
                          data-price={productItem.price}
                        >
                          {productItem.weight}
                          {productItem.weight_symbol} {productItem.name}
                        </option>
                      ))}
                  </select>

                  <input
                    type="number"
                    min="1"
                    value={product.quantity}
                    onChange={(e) =>
                      handleProductChange(
                        index,
                        "quantity",
                        parseInt(e.target.value) || 1
                      )
                    }
                    className="col-span-2 py-3 px-2 bg-gray-100 border-0 rounded-md focus:bg-white focus:ring-2 focus:ring-red-500 outline-none text-center text-sm"
                  />

                  <div className="col-span-2 flex justify-center">
                    <button
                      onClick={() => removeProduct(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors flex-shrink-0"
                      title="Remove product"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Total Price */}
          <div className="bg-gray-50 p-4 rounded-md border-l-4 border-red-600">
            <div className="flex items-center justify-between">
              <span className="text-lg font-semibold text-gray-700">
                Total Price:
              </span>
              <span className="text-2xl font-bold text-red-600">
                ${formData.totalPrice}
              </span>
            </div>
          </div>

          {/* Notes Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <div className="relative">
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange("notes", e.target.value)}
                className="w-full pl-4 pr-10 py-3 bg-gray-100 border-0 rounded-md focus:bg-white focus:ring-2 focus:ring-red-500 outline-none resize-none"
                rows="4"
                placeholder="Special instructions..."
              />
              <div className="absolute right-3 top-4">
                <input
                  type="checkbox"
                  checked={formData.showLabel.notes}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      showLabel: { ...prev.showLabel, notes: e.target.checked },
                    }))
                  }
                  className="w-5 h-5 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 focus:ring-2"
                />
              </div>
            </div>
          </div>

          {/* Print Button */}
          <button
            onClick={handlePrint}
            className="w-full py-4 rounded-full font-bold text-lg text-white bg-red-600 hover:bg-red-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all"
          >
            Print Label
          </button>
        </div>
      </div>
    </>
  );
};

export default CustomPrintLabelPage;
